import type {
  ListNotificationsResponseDto,
  MarkAllReadResponseDto,
  NotificationCountsDto,
  NotificationDto,
  NotificationResponseDto,
} from '@nomiss/sdk';
import { apiConfig } from '@nomiss/configs';
import { ApiRequestError, executeSdkRawRequest } from './api-request.util';
import {
  notificationsApi,
  resolveAccessToken,
  buildApiHeaders,
  fetchWithAuthRecovery,
} from './sdk-client';

export type PushDevicePlatform = 'ios' | 'android' | 'web';

export interface RegisterPushDeviceParams {
  token: string;
  platform: PushDevicePlatform;
  deviceFingerprint: string;
  timezoneId: string;
}

export type NotificationTab = 'all' | 'unread';

export type GetNotificationsParams = {
  tab?: NotificationTab;
  cursor?: string;
  limit?: number;
  accessToken?: string | null;
};

export type NotificationsListResponse = {
  items: NotificationDto[];
  nextCursor: string | null;
  hasMore: boolean;
};

export type NotificationCounts = {
  all: number;
  unread: number;
};

export class NotificationsError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'NotificationsError');
  }
}

function authInitOverrides(
  accessToken?: string | null,
): RequestInit | undefined {
  const token = accessToken ?? resolveAccessToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
}

export async function registerPushDevice(
  params: RegisterPushDeviceParams,
): Promise<void> {
  await notificationsApi.notificationsControllerRegisterDeviceV1({
    registerDeviceTokenDto: params,
  });
}

export async function deactivatePushDevice(token: string): Promise<void> {
  await executeSdkRawRequest({
    request: () =>
      notificationsApi.notificationsControllerDeactivateDeviceV1Raw({
        token,
      }),
    fallback: 'Failed to deactivate push device',
    ErrorClass: NotificationsError,
  });
}

export async function subscribePushTopic(
  token: string,
  topic: string,
): Promise<void> {
  await executeSdkRawRequest({
    request: () =>
      notificationsApi.notificationsControllerSubscribeTopicV1Raw({
        token,
        topic,
      }),
    fallback: 'Failed to subscribe push topic',
    ErrorClass: NotificationsError,
  });
}

export async function getNotifications(
  params: GetNotificationsParams = {},
): Promise<NotificationsListResponse> {
  const { tab = 'all', cursor, limit = 20, accessToken } = params;
  const initOverrides = authInitOverrides(accessToken);

  const data = await executeSdkRawRequest<ListNotificationsResponseDto>({
    request: () =>
      notificationsApi.notificationsControllerListV1Raw(
        {
          tab,
          limit,
          ...(cursor ? { cursor } : {}),
        },
        initOverrides,
      ),
    fallback: 'Failed to load notifications',
    ErrorClass: NotificationsError,
  });

  return {
    items: Array.isArray(data.items) ? data.items : [],
    nextCursor: data.nextCursor ?? null,
    hasMore: data.hasMore === true,
  };
}

export async function getNotificationCounts(
  accessToken?: string | null,
): Promise<NotificationCounts> {
  const initOverrides = authInitOverrides(accessToken);

  const data = await executeSdkRawRequest<NotificationCountsDto>({
    request: () =>
      notificationsApi.notificationsControllerCountsV1Raw(initOverrides),
    fallback: 'Failed to load notification counts',
    ErrorClass: NotificationsError,
  });

  return {
    all: typeof data.all === 'number' ? data.all : 0,
    unread: typeof data.unread === 'number' ? data.unread : 0,
  };
}

export async function markNotificationRead(
  id: string,
  accessToken?: string | null,
): Promise<NotificationResponseDto> {
  const initOverrides = authInitOverrides(accessToken);

  return executeSdkRawRequest<NotificationResponseDto>({
    request: () =>
      notificationsApi.notificationsControllerMarkReadV1Raw(
        { id },
        initOverrides,
      ),
    fallback: 'Failed to mark notification as read',
    ErrorClass: NotificationsError,
  });
}

export async function markAllNotificationsRead(
  accessToken?: string | null,
): Promise<MarkAllReadResponseDto> {
  const initOverrides = authInitOverrides(accessToken);

  return executeSdkRawRequest<MarkAllReadResponseDto>({
    request: () =>
      notificationsApi.notificationsControllerReadAllV1Raw(initOverrides),
    fallback: 'Failed to mark all notifications as read',
    ErrorClass: NotificationsError,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

export type AdminAnnouncementInput = {
  title: string;
  body: string;
  dedupeKey?: string;
};

export type AdminAnnouncementResult = {
  message: string;
  broadcastId?: string;
  topic?: string;
};

export type AdminAnnouncementHistoryItem = {
  id: string;
  title: string;
  body: string;
  dedupeKey: string | null;
  sentAt: string | null;
  createdAt: string;
  createdBy: string | null;
  createdByName: string | null;
  createdByEmail: string | null;
};

export type AdminAnnouncementHistoryParams = {
  page?: number;
  limit?: number;
};

export type AdminAnnouncementHistoryMeta = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export type AdminAnnouncementHistoryResult = {
  data: AdminAnnouncementHistoryItem[];
  meta: AdminAnnouncementHistoryMeta;
};

function parseHistoryMeta(value: unknown): AdminAnnouncementHistoryMeta {
  const meta = isRecord(value) ? value : {};
  const page = typeof meta.page === 'number' ? meta.page : 1;
  const limit = typeof meta.limit === 'number' ? meta.limit : 20;
  const total = typeof meta.total === 'number' ? meta.total : 0;
  const totalPages =
    typeof meta.totalPages === 'number'
      ? meta.totalPages
      : total === 0
        ? 0
        : Math.ceil(total / limit);
  return { page, limit, total, totalPages };
}

function parseHistoryItem(value: unknown): AdminAnnouncementHistoryItem | null {
  if (!isRecord(value) || typeof value.id !== 'string') return null;
  return {
    id: value.id,
    title: asString(value.title),
    body: asString(value.body),
    dedupeKey: typeof value.dedupeKey === 'string' ? value.dedupeKey : null,
    sentAt: typeof value.sentAt === 'string' ? value.sentAt : null,
    createdAt: asString(value.createdAt),
    createdBy: typeof value.createdBy === 'string' ? value.createdBy : null,
    createdByName:
      typeof value.createdByName === 'string' ? value.createdByName : null,
    createdByEmail:
      typeof value.createdByEmail === 'string' ? value.createdByEmail : null,
  };
}

export async function sendAdminAnnouncement(
  input: AdminAnnouncementInput,
): Promise<AdminAnnouncementResult> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/notifications/admin/announcement`,
    {
      method: 'POST',
      headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(input),
    },
  );

  if (!response.ok) {
    const json = (await response.json().catch(() => ({}))) as {
      message?: string | string[];
    };
    const rawMessage = json.message;
    const message = Array.isArray(rawMessage)
      ? rawMessage.join(', ')
      : typeof rawMessage === 'string'
        ? rawMessage
        : 'Failed to send announcement';
    throw new NotificationsError(response.status, message);
  }

  const json = (await response.json()) as unknown;
  const envelope = isRecord(json) ? json : {};
  const data = isRecord(envelope.data) ? envelope.data : {};

  return {
    message: asString(envelope.message, 'Announcement sent'),
    broadcastId:
      typeof data.broadcastId === 'string' ? data.broadcastId : undefined,
    topic: typeof data.topic === 'string' ? data.topic : undefined,
  };
}

export async function listAdminAnnouncements(
  params: AdminAnnouncementHistoryParams = {},
): Promise<AdminAnnouncementHistoryResult> {
  const search = new URLSearchParams();
  if (params.page != null) search.set('page', String(params.page));
  if (params.limit != null) search.set('limit', String(params.limit));
  const query = search.toString();

  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/notifications/admin/announcements${
      query ? `?${query}` : ''
    }`,
    {
      method: 'GET',
      headers: buildApiHeaders(),
    },
  );

  if (!response.ok) {
    const json = (await response.json().catch(() => ({}))) as {
      message?: string | string[];
    };
    const rawMessage = json.message;
    const message = Array.isArray(rawMessage)
      ? rawMessage.join(', ')
      : typeof rawMessage === 'string'
        ? rawMessage
        : 'Failed to load announcement history';
    throw new NotificationsError(response.status, message);
  }

  const json = (await response.json()) as unknown;
  const envelope = isRecord(json) ? json : {};
  const rows = Array.isArray(envelope.data) ? envelope.data : [];

  return {
    data: rows
      .map(parseHistoryItem)
      .filter((row): row is AdminAnnouncementHistoryItem => row != null),
    meta: parseHistoryMeta(envelope.meta),
  };
}
